// src/components/reports/PromptsTab.jsx
import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Copy,
  Check,
  Bot,
  ThumbsUp,
  ThumbsDown,
  Search,
  Download,
  Sparkles,
  Clock,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Filter,
  Database,
  TrendingUp,
  ArrowRight,
  Info,
  Edit3,
  Link as LinkIcon,
  Target,
  BarChart3,
  Layers,
} from 'lucide-react';
import { formatIndustryLabel, humanizeSlug } from '../../utils/reportHelpers';

const MODEL_CONFIG = {
  ChatGPT: {
    bg: 'bg-blue-500/20',
    text: 'text-[#3b82f6]',
    icon: '🤖',
    url: (p) =>
      `https://chat.openai.com/?q=${encodeURIComponent(p.slice(0, 200))}`,
  },
  Claude: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    icon: '🦜',
    url: (p) =>
      `https://claude.ai/new?q=${encodeURIComponent(p.slice(0, 200))}`,
  },
  Gemini: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    icon: '✨',
    url: (p) =>
      `https://gemini.google.com/app?q=${encodeURIComponent(p.slice(0, 200))}`,
  },
  Grok: {
    bg: 'bg-rose-500/20',
    text: 'text-rose-400',
    icon: '⚡',
    url: (p) => `https://grok.com/?q=${encodeURIComponent(p.slice(0, 200))}`,
  },
};

const DIFFICULTY_CONFIG = {
  Beginner: {
    bg: 'bg-blue-500/20',
    text: 'text-[#3b82f6]',
    icon: '🌱',
  },
  Intermediate: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    icon: '📊',
  },
  Advanced: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    icon: '⚡',
  },
  Expert: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    icon: '👑',
  },
};

const IMPACT_CONFIG = {
  High: { color: 'bg-blue-500', label: 'High Impact', width: 'w-full' },
  Medium: { color: 'bg-amber-500', label: 'Medium Impact', width: 'w-2/3' },
  Low: { color: 'bg-slate-500', label: 'Low Impact', width: 'w-1/3' },
};

const IMPACT_ORDER = { High: 0, Medium: 1, Low: 2 };

const extractVariables = (text) => {
  const matches = text.match(/\[[A-Z_]+\]/g);
  return matches ? [...new Set(matches)] : [];
};

const fillVariables = (text, vars) => {
  let filled = text;
  Object.entries(vars).forEach(([key, val]) => {
    if (val.trim()) filled = filled.replaceAll(key, val);
  });
  return filled;
};

const HighlightedPrompt = ({ text, filledVars = {} }) => {
  const parts = text.split(/(\[[A-Z_]+\])/g);
  return (
    <span>
      {parts.map((part, i) => {
        if (/^\[[A-Z_]+\]$/.test(part)) {
          const filled = filledVars[part];
          return filled ? (
            <span
              key={i}
              className="bg-blue-500/20 text-blue-300 rounded px-1 font-semibold border border-blue-500/30"
            >
              {filled}
            </span>
          ) : (
            <span
              key={i}
              className="bg-amber-500/20 text-amber-300 rounded px-1 font-semibold border border-amber-500/30"
            >
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
};

const VariableFiller = ({ variables, values, onChange }) => (
  <div className="mt-3 bg-amber-900/10 border border-amber-500/20 rounded-xl p-4 space-y-2">
    <div className="flex items-center gap-2 mb-3">
      <Edit3 className="w-4 h-4 text-amber-400" />
      <span className="text-xs font-semibold text-amber-300">
        Fill in your details once — every prompt becomes “for your business”
      </span>
    </div>
    <div className="grid sm:grid-cols-2 gap-2">
      {variables.map((v) => (
        <div key={v}>
          <label
            htmlFor={`var-${v}`}
            className="text-xs text-slate-500 mb-1 block"
          >
            {v}
          </label>
          <input
            id={`var-${v}`}
            type="text"
            placeholder={`Enter ${v
              .replace(/[\[\]]/g, '')
              .toLowerCase()
              .replace(/_/g, ' ')}`}
            value={values[v] || ''}
            onChange={(e) => onChange(v, e.target.value)}
            className="w-full bg-slate-950/50 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/50"
            aria-label={`Fill variable ${v}`}
          />
        </div>
      ))}
    </div>
  </div>
);

const ImpactBar = ({ impact }) => {
  const config = IMPACT_CONFIG[impact] || IMPACT_CONFIG.Medium;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-16 shrink-0">
        {config.label}
      </span>
      <div className="flex-1 bg-white/8 rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-full rounded-full ${config.color} ${config.width} transition-all`}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

// ── PromptCard: “Prompt Plays” ────────────────────────────────────────────────
const PromptCard = React.memo(
  ({
    prompt,
    index,
    copiedPrompt,
    copyToClipboard,
    usedPrompts,
    markUsed,
    ratings,
    ratePrompt,
    allPrompts,
    reportId,
    globalVars = {},
    onGlobalVarChange,
    t = (key) => key.split('.').pop(),
  }) => {
    const [expanded, setExpanded] = useState(true);
    const [showWhy, setShowWhy] = useState(false);
    const [localVars, setLocalVars] = useState({});
    // Merge global vars with local overrides
    const filledVars = useMemo(() => ({ ...globalVars, ...localVars }), [globalVars, localVars]);
    const _noteKey = reportId ? `prompt_outcome_${reportId}_${prompt.id}` : null;
    const [outcomeNote, setOutcomeNote] = useState(() => {
      if (!_noteKey) return '';
      try { return localStorage.getItem(_noteKey) || ''; } catch { return ''; }
    });
    const isUsed = usedPrompts.has(prompt.id);
    const rating = ratings[prompt.id];

    // Persist outcome note to localStorage whenever it changes
    React.useEffect(() => {
      if (!_noteKey) return;
      try {
        if (outcomeNote) localStorage.setItem(_noteKey, outcomeNote);
        else localStorage.removeItem(_noteKey);
      } catch { /* storage full — silently skip */ }
    }, [outcomeNote, _noteKey]);

    const supportedModels = prompt.supportedModels?.length
      ? prompt.supportedModels
      : ['ChatGPT', 'Claude', 'Gemini', 'Grok'];
    const bestModel =
      prompt.bestModel || supportedModels[0] || 'ChatGPT';
    const modelStyle =
      MODEL_CONFIG[bestModel] ?? MODEL_CONFIG.ChatGPT;
    const diffStyle =
      DIFFICULTY_CONFIG[prompt.difficulty] ??
      DIFFICULTY_CONFIG.Beginner;

    const variables = useMemo(
      () => extractVariables(prompt.prompt),
      [prompt.prompt]
    );
    const hasVariables = variables.length > 0;
    const finalPrompt = hasVariables
      ? fillVariables(prompt.prompt, filledVars)
      : prompt.prompt;
    const allFilled = hasVariables
      ? variables.every((v) => filledVars[v]?.trim())
      : true;

    const chainSuggestions = useMemo(() => {
      if (!prompt.chainWith?.length || !allPrompts) return [];
      return prompt.chainWith
        .map((id) => allPrompts.find((p) => p.id === id))
        .filter(Boolean)
        .slice(0, 2);
    }, [prompt.chainWith, allPrompts]);

    const handleCopy = useCallback(() => {
      copyToClipboard(finalPrompt, prompt.id);
      markUsed(prompt.id);
    }, [finalPrompt, prompt.id, copyToClipboard, markUsed]);

    const handleVarChange = useCallback((key, val) => {
      // If there's a global handler and this variable exists globally, update globally
      if (onGlobalVarChange && globalVars[key] !== undefined) {
        onGlobalVarChange(key, val);
      } else {
        setLocalVars((prev) => ({ ...prev, [key]: val }));
      }
    }, [onGlobalVarChange, globalVars]);

    const handleRateUp = useCallback(
      () => ratePrompt(prompt.id, 'up'),
      [prompt.id, ratePrompt]
    );
    const handleRateDown = useCallback(
      () => ratePrompt(prompt.id, 'down'),
      [prompt.id, ratePrompt]
    );

    return (
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.04 }}
        className={`bg-slate-900/70 backdrop-blur-xl border rounded-2xl overflow-hidden transition-all ${
          isUsed ? 'border-white/10' : 'border-white/10'
        }`}
        aria-labelledby={`prompt-title-${prompt.id}`}
      >
        {/* Header */}
        <div className="p-6 pb-3">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                <h3
                  id={`prompt-title-${prompt.id}`}
                  className="font-bold text-lg text-white"
                >
                  {prompt.title}
                </h3>
                {isUsed && (
                  <Badge className="bg-blue-500/20 text-[#3b82f6] text-xs">
                    <Check className="w-3 h-3 mr-1" /> Used
                  </Badge>
                )}
                {prompt.roiLabel && (
                  <Badge className="bg-blue-500/20 text-[#3b82f6] text-xs border border-blue-500/30">
                    <TrendingUp className="w-3 h-3 mr-1" />{' '}
                    {prompt.roiLabel}
                  </Badge>
                )}
                {prompt.playId && (
                  <Badge className="bg-indigo-500/20 text-indigo-300 text-[10px] border border-indigo-500/40">
                    Prompt Play #{prompt.playId}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-400 mb-2">
                {prompt.description}
              </p>

              {prompt.impact && <ImpactBar impact={prompt.impact} />}

              <div className="flex flex-wrap gap-2 mt-2">
                {prompt.category && (
                  <Badge className="bg-white/8 text-slate-300 text-xs border border-white/8">
                    {humanizeSlug(prompt.category)}
                  </Badge>
                )}
                <Badge
                  className={`text-xs ${modelStyle.bg} ${modelStyle.text}`}
                >
                  <Bot className="w-3 h-3 mr-1" /> Best: {bestModel}
                </Badge>
                {prompt.difficulty && (
                  <Badge
                    className={`text-xs ${diffStyle.bg} ${diffStyle.text}`}
                  >
                    {diffStyle.icon} {prompt.difficulty}
                  </Badge>
                )}
                {prompt.estimatedTime && (
                  <Badge className="bg-white/8 text-slate-400 text-xs border border-white/8">
                    <Clock className="w-3 h-3 mr-1" />{' '}
                    {prompt.estimatedTime}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className={`text-slate-300 hover:text-white ${
                  !allFilled && hasVariables ? 'opacity-50' : ''
                }`}
                disabled={hasVariables && !allFilled}
                aria-label={
                  copiedPrompt === prompt.id ? 'Copied' : 'Copy prompt'
                }
              >
                {copiedPrompt === prompt.id ? (
                  <Check className="h-4 w-4 text-[#3b82f6]" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded((e) => !e)}
                className="text-slate-400"
                aria-label={expanded ? 'Collapse prompt' : 'Expand prompt'}
              >
                {expanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Body */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              style={{ overflow: 'hidden' }}
            >
              <div className="px-6 pb-5 space-y-3">
                {/* Context Layer */}
                <div className="bg-slate-950/60 border border-white/8 rounded-xl p-3 text-xs text-slate-300 flex items-start gap-3">
                  <Layers className="w-4 h-4 text-[#3b82f6] mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-100 mb-1">
                      Context Layer
                    </p>
                    <p>
                      This prompt is calibrated for your answers in{' '}
                      <span className="text-blue-300">
                        Diagnosis
                      </span>{' '}
                      and your{' '}
                      <span className="text-blue-300">tool stack</span>.
                      Use it as a “command” that tells the AI exactly how
                      your business operates — not a generic template.
                    </p>
                  </div>
                </div>

                {hasVariables && (
                  <VariableFiller
                    variables={variables}
                    values={filledVars}
                    onChange={handleVarChange}
                  />
                )}

                <pre className="bg-slate-950/90 text-slate-100 p-4 rounded-xl text-sm overflow-auto whitespace-pre-wrap font-mono leading-relaxed border border-white/8">
                  <HighlightedPrompt
                    text={prompt.prompt}
                    filledVars={filledVars}
                  />
                </pre>

                {/* Primary CTA */}
                <Button
                  onClick={handleCopy}
                  size="sm"
                  className={`w-full gap-2 ${
                    allFilled || !hasVariables
                      ? 'bg-blue-500 hover:bg-blue-500 text-white'
                      : 'bg-slate-900/70 text-slate-500 cursor-not-allowed'
                  }`}
                  disabled={hasVariables && !allFilled}
                >
                  {copiedPrompt === prompt.id ? (
                    <>
                      <Check className="w-4 h-4" /> {t('report.prompts.copied')}
                    </>
                  ) : hasVariables && !allFilled ? (
                    <>
                      <Edit3 className="w-4 h-4" /> {t('report.prompts.copy')}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />{' '}
                      {allFilled && hasVariables
                        ? t('report.prompts.copyPersonalized')
                        : t('report.prompts.copy')}
                    </>
                  )}
                </Button>

                {/* Open in models */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {t('report.prompts.runIn')}
                  </span>
                  {supportedModels.map((model) => {
                    const style = MODEL_CONFIG[model];
                    if (!style) return null;
                    return (
                      <a
                        key={model}
                        href={style.url(finalPrompt)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => markUsed(prompt.id)}
                        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-white/8 hover:border-white/20 transition-colors ${style.text}`}
                        aria-label={`Open in ${model}`}
                      >
                        <span>{style.icon}</span> {model}
                      </a>
                    );
                  })}
                </div>

                {/* Why it works / meta */}
                {prompt.whyItWorks && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowWhy((w) => !w)}
                      className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300"
                      aria-expanded={showWhy}
                    >
                      <Info className="w-3.5 h-3.5" />
                      {showWhy
                        ? 'Hide why this works'
                        : t('report.prompts.whyWorks')}
                      {showWhy ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                    <AnimatePresence>
                      {showWhy && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <div className="mt-2 bg-blue-900/20 border border-blue-500/20 rounded-xl p-3 text-xs text-blue-300 leading-relaxed">
                            {prompt.whyItWorks}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {prompt.proTip && (
                  <div className="flex items-start gap-2 bg-blue-900/20 border border-blue-500/20 rounded-xl p-3">
                    <Sparkles className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-300">
                      <span className="font-semibold">{t('report.prompts.proTip')}</span>{' '}
                      {prompt.proTip}
                    </p>
                  </div>
                )}

                {/* Chain-of-prompts */}
                {chainSuggestions.length > 0 && (
                  <div className="bg-purple-900/10 border border-purple-500/20 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <ArrowRight className="w-3.5 h-3.5 text-purple-400" />
                      <span className="text-xs font-semibold text-purple-300">
                        Run these next to complete the Prompt Play:
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {chainSuggestions.map((p) => (
                        <span
                          key={p.id}
                          className="text-xs bg-purple-800/30 text-purple-300 border border-purple-500/20 rounded-full px-2 py-0.5"
                        >
                          {p.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Outcome tracker (proprietary feel) */}
                <div className="bg-slate-950/50 border border-white/8 rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <BarChart3 className="w-3.5 h-3.5 text-[#3b82f6]" />
                    <span className="font-semibold text-slate-200">
                      {t('report.prompts.logOutcome')}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    After you run this prompt, jot down any time saved or
                    revenue impact. Over a month, this becomes your own
                    private ROI dataset.
                  </p>
                  <textarea
                    value={outcomeNote}
                    onChange={(e) => setOutcomeNote(e.target.value)}
                    rows={2}
                    className="w-full bg-slate-950/80 border border-white/8 rounded-lg px-3 py-1.5 text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50"
                    placeholder="E.g. “Cut support replies by 20% this week” or “Closed 3 extra orders from this sequence.”"
                  />
                </div>

                {/* Rating */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">
                      Helpful?
                    </span>
                    <button
                      type="button"
                      onClick={handleRateUp}
                      className={`p-1 rounded transition-colors ${
                        rating === 'up'
                          ? 'text-[#3b82f6]'
                          : 'text-slate-600 hover:text-slate-400'
                      }`}
                      aria-label="Thumbs up"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleRateDown}
                      className={`p-1 rounded transition-colors ${
                        rating === 'down'
                          ? 'text-red-400'
                          : 'text-slate-600 hover:text-slate-400'
                      }`}
                      aria-label="Thumbs down"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                  </div>
                  {prompt.source && (
                    <div className="text-[11px] text-slate-600 flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> {prompt.source}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.article>
    );
  }
);

PromptCard.displayName = 'PromptCard';

// ── MAIN TAB ──────────────────────────────────────────────────────────────────
const _tFallback = (key) => key.split('.').pop();
const PromptsTab = ({
  promptPack = [],
  copiedPrompt,
  copyToClipboard,
  reportId,
  industry,
  subVertical,
  onRatingSubmit,
  promptPackIntro,
  assistantLabel = 'your AI assistant',
  t = _tFallback,
}) => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeModel, setActiveModel] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  const [usedPrompts, setUsedPrompts] = useState(() => {
    try {
      const stored = localStorage.getItem(
        `used_prompts_${reportId}`
      );
      return new Set(stored ? JSON.parse(stored) : []);
    } catch {
      return new Set();
    }
  });

  // Global variable filler — all unique [VARIABLES] across all prompts
  const allVariables = useMemo(() => {
    const vars = new Set();
    promptPack.forEach(p => extractVariables(p.prompt).forEach(v => vars.add(v)));
    return [...vars].sort();
  }, [promptPack]);

  const [globalVars, setGlobalVars] = useState(() => {
    try {
      const stored = localStorage.getItem(`global_vars_${reportId}`);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });
  const [showGlobalFiller, setShowGlobalFiller] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [nextPromptNudge, setNextPromptNudge] = useState(null);

  const handleGlobalVarChange = useCallback((key, val) => {
    setGlobalVars(prev => {
      const next = { ...prev, [key]: val };
      try { localStorage.setItem(`global_vars_${reportId}`, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [reportId]);

  const [ratings, setRatings] = useState(() => {
    try {
      const stored = localStorage.getItem(
        `prompt_ratings_${reportId}`
      );
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const categories = useMemo(
    () => ['All', ...new Set(promptPack.map((p) => p.category).filter(Boolean))],
    [promptPack]
  );

  const models = useMemo(
    () => [
      'All',
      ...new Set(
        promptPack.flatMap((p) =>
          p.supportedModels?.length
            ? p.supportedModels
            : ['ChatGPT', 'Claude', 'Gemini', 'Grok']
        )
      ),
    ],
    [promptPack]
  );

  const filtered = useMemo(
    () =>
      promptPack
        .filter((p) => {
          const matchesCategory =
            activeCategory === 'All' || p.category === activeCategory;
          const candidateModels =
            p.supportedModels?.length
              ? p.supportedModels
              : ['ChatGPT', 'Claude', 'Gemini', 'Grok'];
          const matchesModel =
            activeModel === 'All' ||
            candidateModels.includes(activeModel);
          const q = search.toLowerCase();
          const matchesSearch =
            !search.trim() ||
            p.title?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q) ||
            p.prompt?.toLowerCase().includes(q);
          return matchesCategory && matchesModel && matchesSearch;
        })
        .sort(
          (a, b) =>
            (IMPACT_ORDER[a.impact] ?? 1) -
            (IMPACT_ORDER[b.impact] ?? 1)
        ),
    [promptPack, activeCategory, activeModel, search]
  );

  const firstPlay = useMemo(
    () => promptPack.find(p => p.impact === 'High') ?? null,
    [promptPack]
  );

  const filteredList = useMemo(
    () => firstPlay ? filtered.filter(p => p.id !== firstPlay.id) : filtered,
    [filtered, firstPlay]
  );

  const sequenceGroups = useMemo(() => {
    if (viewMode !== 'sequence') return [];
    const groups = new Map();
    filteredList.forEach(p => {
      const key = p.playId != null ? String(p.playId) : `__solo_${p.id}`;
      if (!groups.has(key)) groups.set(key, { playId: p.playId, category: p.category, prompts: [] });
      groups.get(key).prompts.push(p);
    });
    return [...groups.values()];
  }, [filteredList, viewMode]);

  const usedCount = useMemo(
    () =>
      [...usedPrompts].filter((id) =>
        promptPack.some((p) => p.id === id)
      ).length,
    [usedPrompts, promptPack]
  );

  const markUsed = useCallback(
    (id) => {
      setUsedPrompts((prev) => {
        const next = new Set(prev).add(id);
        try {
          localStorage.setItem(
            `used_prompts_${reportId}`,
            JSON.stringify([...next])
          );
        } catch {}
        return next;
      });
      const usedPrompt = promptPack.find(p => p.id === id);
      if (usedPrompt?.chainWith?.length) {
        const nextChain = usedPrompt.chainWith
          .map(cid => promptPack.find(p => p.id === cid))
          .filter(Boolean)
          .find(p => !usedPrompts.has(p.id));
        if (nextChain) {
          setNextPromptNudge(nextChain);
          setTimeout(() => setNextPromptNudge(null), 4000);
        }
      }
    },
    [reportId, promptPack, usedPrompts]
  );

  const ratePrompt = useCallback(
    (id, value) => {
      setRatings((prev) => {
        const next = {
          ...prev,
          [id]: prev[id] === value ? null : value,
        };
        try {
          localStorage.setItem(
            `prompt_ratings_${reportId}`,
            JSON.stringify(next)
          );
        } catch {}
        if (onRatingSubmit) onRatingSubmit(id, next[id]);
        return next;
      });
    },
    [reportId, onRatingSubmit]
  );

  const exportPrompts = useCallback(() => {
    const content = promptPack
      .map(
        (p, i) =>
          `=== ${i + 1}. ${p.title} ===
${p.description || ''}
${p.roiLabel ? `ROI: ${p.roiLabel}\n` : ''}${
            p.estimatedTime ? `Time: ${p.estimatedTime}\n` : ''
          }PROMPT:
${p.prompt}
${p.proTip ? `\nPRO TIP: ${p.proTip}\n` : ''}`
      )
      .join('\n' + '─'.repeat(60) + '\n\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AI_Prompt_Plays_${industry || 'Business'}_${new Date()
      .toISOString()
      .slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [promptPack, industry]);

  if (!promptPack.length) {
    return (
      <div className="text-center py-16 text-slate-400 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl">
        <Database className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p className="text-lg">{t('report.prompts.empty')}</p>
        <p className="text-sm mt-2">
          {t('report.prompts.emptyDesc')}
        </p>
      </div>
    );
  }

  const progressPct = Math.round(
    (usedCount / promptPack.length) * 100
  );
  const modelName = assistantLabel;

  return (
    <div className="space-y-6">
      {/* Proprietary banner */}
      <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm text-white font-medium mb-0.5">
              AI Prompt Operating System for {industry ? formatIndustryLabel(industry, 'your business', subVertical) : 'your business'}
            </p>
            <p className="text-xs text-slate-400">
              {promptPackIntro ||
                `${promptPack.length} “Prompt Plays” generated from your answers, your roadmap, and our SMB dataset. They are not sold or published anywhere else — this is your private command center for ${modelName}.`}
            </p>
          </div>
        </div>
      </div>

      {/* Header + progress */}
      <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">
              {t('report.prompts.title')}
            </h3>
            <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 border border-amber-500/30 text-xs">
              {/* R31.7: removed the unsourced "$X if built by a consultant"
                  multiplier (Sarah R34c-adjacent finding — same defect class
                  as R28.1 hardcoded ROI numbers). Replaced with a
                  defensible time-to-deploy claim sourced from the actual
                  pack length — N prompts × ~30min consultant draft per
                  prompt = N/2 hours, rounded for readability. */}
              💰 {promptPack.length} ready-to-paste prompts —
              {' '}saves ~{Math.max(2, Math.round(promptPack.length / 2))} hours of drafting
            </Badge>
            <p className="text-xs text-slate-400 mt-1">
              Designed to paste directly into {modelName}, but also
              works in other leading assistants.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportPrompts}
            className="border-white/15 text-slate-300 hover:text-white hover:border-white/30 gap-2 rounded-full"
          >
            <Download className="w-4 h-4" /> {t('report.prompts.export')}
          </Button>
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-xs text-slate-500">
            <span>Implementation progress</span>
            <span className="text-[#3b82f6] font-medium">
              {usedCount}/{promptPack.length} prompts used
            </span>
          </div>
          <div className="w-full bg-white/8 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-teal-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>

          {usedCount === 0 && (
            <p className="text-xs text-slate-600">
              Start with Prompt Play #1 below — you can be live in the
              next 10 minutes.
            </p>
          )}
          {usedCount === 1 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4 text-center"
            >
              <p className="text-[#3b82f6] font-semibold text-sm">
                🚀 First Prompt Play used! Most teams see a measurable
                lift within 24–72 hours.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Keep going — the real ROI compounds when you connect
                multiple plays together.
              </p>
            </motion.div>
          )}
          {usedCount > 1 &&
            usedCount < promptPack.length && (
              <p className="text-xs text-slate-500">
                <span className="text-[#3b82f6]">
                  Nice momentum.
                </span>{' '}
                Aim to implement at least 1 new prompt per week.
              </p>
            )}
          {usedCount === promptPack.length && (
            <p className="text-xs text-[#3b82f6] font-medium">
              🎉 All Prompt Plays used! You’re getting full value from
              this blueprint.
            </p>
          )}
        </div>
      </div>

      {/* Global variable filler panel */}
      {allVariables.length > 0 && (
        <div className="border border-amber-500/25 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowGlobalFiller(f => !f)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-amber-900/10 hover:bg-amber-900/20 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <span className="text-sm font-semibold text-amber-300">
                Fill your details once — all {promptPack.length} prompts auto-personalize
              </span>
              {Object.values(globalVars).filter(v => v?.trim()).length > 0 && (
                <span className="text-[10px] bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-2 py-0.5 rounded-full">
                  {Object.values(globalVars).filter(v => v?.trim()).length}/{allVariables.length} filled
                </span>
              )}
            </div>
            {showGlobalFiller ? <ChevronUp className="w-4 h-4 text-amber-400" /> : <ChevronDown className="w-4 h-4 text-amber-400" />}
          </button>
          <AnimatePresence>
            {showGlobalFiller && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <div className="px-4 pb-4 pt-2 bg-amber-900/5 border-t border-amber-500/15">
                  <p className="text-xs text-slate-500 mb-3">
                    These values replace <span className="text-amber-300 font-mono">[PLACEHOLDERS]</span> in every prompt — no need to re-enter per card.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {allVariables.map(v => (
                      <div key={v}>
                        <label className="text-xs text-slate-500 mb-1 block">{v}</label>
                        <input
                          type="text"
                          placeholder={`Enter ${v.replace(/[\[\]]/g, '').toLowerCase().replace(/_/g, ' ')}`}
                          value={globalVars[v] || ''}
                          onChange={e => handleGlobalVarChange(v, e.target.value)}
                          className="w-full bg-slate-950/50 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/50"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Featured "First Play" */}
      {firstPlay && (
        <div>
          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Start here — highest impact for your business
          </p>
          <div className="border border-amber-500/40 rounded-2xl bg-amber-500/5 relative">
            <span className="absolute top-4 left-4 z-10 text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5">
              Your First Play
            </span>
            <PromptCard
              prompt={firstPlay}
              index={0}
              copiedPrompt={copiedPrompt}
              copyToClipboard={copyToClipboard}
              usedPrompts={usedPrompts}
              markUsed={markUsed}
              ratings={ratings}
              ratePrompt={ratePrompt}
              allPrompts={promptPack}
              reportId={reportId}
              globalVars={globalVars}
              onGlobalVarChange={handleGlobalVarChange}
              t={t}
            />
          </div>
        </div>
      )}

      {/* Search + filters */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder={t('report.prompts.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-950/50 border-white/15 text-white placeholder:text-slate-500"
              aria-label="Search prompts"
            />
          </div>
          {(categories.length > 2 || models.length > 2) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters((f) => !f)}
              className="border-white/15 text-slate-300 gap-2 rounded-full"
              aria-expanded={showFilters}
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </Button>
          )}
          <div className="flex rounded-full border border-white/15 overflow-hidden flex-shrink-0">
            {['cards', 'sequence'].map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => setViewMode(mode)}
                className={`text-xs px-3 py-1.5 capitalize transition-colors ${
                  viewMode === mode
                    ? 'bg-blue-500/20 text-[#3b82f6]'
                    : 'text-slate-400 hover:text-white'
                }`}
                aria-pressed={viewMode === mode}
              >
                {mode === 'cards' ? 'Cards' : 'Sequence'}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              style={{ overflow: 'hidden' }}
              className="bg-slate-900/70 backdrop-blur-xl rounded-xl p-4 border border-white/10"
            >
              <div className="grid md:grid-cols-2 gap-4">
                {categories.length > 2 && (
                  <div>
                    <label className="text-xs text-slate-400 mb-2 block">
                      Category
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setActiveCategory(cat)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            activeCategory === cat
                              ? 'bg-blue-500 border-blue-500 text-white'
                              : 'bg-slate-900/70 border-white/10 text-slate-400 hover:border-white/20'
                          }`}
                          aria-pressed={activeCategory === cat}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {models.length > 2 && (
                  <div>
                    <label className="text-xs text-slate-400 mb-2 block">
                      AI Model
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {models.map((model) => (
                        <button
                          key={model}
                          type="button"
                          onClick={() => setActiveModel(model)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            activeModel === model
                              ? 'bg-purple-600 border-purple-500 text-white'
                              : 'bg-slate-900/70 border-white/10 text-slate-400 hover:border-white/20'
                          }`}
                          aria-pressed={activeModel === model}
                        >
                          {model}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Results count */}
      <div className="flex justify-between text-sm text-slate-500">
        <span>
          {filtered.length} of {promptPack.length} Prompt Plays
        </span>
        {usedCount > 0 && (
          <span className="text-[#3b82f6]">
            {usedCount} used · {100 - progressPct}% remaining
          </span>
        )}
      </div>

      {/* Cards / Sequence */}
      {filteredList.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl">
          <Search className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p>{t('report.prompts.noResults')}</p>
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setActiveCategory('All');
              setActiveModel('All');
            }}
            className="mt-2 text-sm text-[#3b82f6] hover:text-blue-300"
          >
            {t('report.prompts.clearFilters')}
          </button>
        </div>
      ) : viewMode === 'sequence' ? (
        <div className="space-y-8">
          {sequenceGroups.map((group, gi) => (
            <div key={group.playId ?? `group-${gi}`}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-indigo-400" />
                Play: {group.playId ?? group.category ?? 'Standalone'}
              </p>
              <div className="space-y-2">
                {group.prompts.map((prompt, si) => (
                  <div key={prompt.id} className="flex flex-col">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {si + 1}
                      </span>
                      <span className="text-xs text-slate-500">Step {si + 1}</span>
                    </div>
                    <PromptCard
                      prompt={prompt}
                      index={gi * 100 + si}
                      copiedPrompt={copiedPrompt}
                      copyToClipboard={copyToClipboard}
                      usedPrompts={usedPrompts}
                      markUsed={markUsed}
                      ratings={ratings}
                      ratePrompt={ratePrompt}
                      allPrompts={promptPack}
                      reportId={reportId}
                      globalVars={globalVars}
                      onGlobalVarChange={handleGlobalVarChange}
                      t={t}
                    />
                    {si < group.prompts.length - 1 && (
                      <div className="flex justify-center my-1">
                        <ArrowRight className="w-4 h-4 text-slate-600 rotate-90" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredList.map((prompt, i) => (
            <PromptCard
              key={prompt.id}
              prompt={prompt}
              index={i}
              copiedPrompt={copiedPrompt}
              copyToClipboard={copyToClipboard}
              usedPrompts={usedPrompts}
              markUsed={markUsed}
              ratings={ratings}
              ratePrompt={ratePrompt}
              allPrompts={promptPack}
              reportId={reportId}
              globalVars={globalVars}
              onGlobalVarChange={handleGlobalVarChange}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Chain nudge banner */}
      <AnimatePresence>
        {nextPromptNudge && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 border border-purple-500/40 rounded-2xl px-5 py-3 shadow-xl flex items-center gap-3"
          >
            <ArrowRight className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <p className="text-sm text-slate-200">
              Next in this sequence:{' '}
              <span className="font-semibold text-purple-300">{nextPromptNudge.title}</span>{' '}
              →
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Value anchor */}
      <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-4">
        <p className="text-xs text-slate-500 text-center mb-2">
          What this Prompt Library would cost elsewhere:
        </p>
        <div className="flex justify-around text-center">
          <div>
            <p className="text-white font-bold text-sm">$400–800</p>
            <p className="text-xs text-slate-600">
              Freelance prompt engineer
            </p>
          </div>
          <div>
            <p className="text-white font-bold text-sm">$1,200+</p>
            <p className="text-xs text-slate-600">
              AI consultant (hourly)
            </p>
          </div>
          <div>
            <p className="text-[#3b82f6] font-bold text-sm">Included</p>
            <p className="text-xs text-slate-600">With your blueprint</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptsTab;
